import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { OrderHistoryLogicReturn } from './OrderHistory.types';
import { 
  fetchCheckouts,
  selectCheckoutsState,
  selectIsUserLoggedIn,
  selectIsAdminLoggedIn,
  addToCart,
  selectUser
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 디바이스 타입을 감지하는 훅
 */
const useDeviceDetect = () => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 576) {
        setDeviceType('mobile');
      } else if (width < 992) {
        setDeviceType('tablet');
      } else {
        setDeviceType('desktop');
      }
    };

    // 초기화
    handleResize();

    // 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);

    // 클린업
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return deviceType;
};

/**
 * 주문 내역 컴포넌트 로직 훅
 */
const useOrderHistoryLogic = (componentData: ComponentData, mode: ComponentRenderMode): OrderHistoryLogicReturn => {
  // 호환성을 위한 props 변환
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  // Redux 상태
  const { checkouts, loading, error, pagination } = useSelector(selectCheckoutsState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const user = useSelector(selectUser);
  // const withcookieData = useSelector(selectWithcookieData); // 현재 사용하지 않음
  
  // 디바이스 타입 감지
  const deviceType = useDeviceDetect();
  const isMobile = deviceType === 'mobile';
  
  // 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  // const [processedOrders, setProcessedOrders] = useState<any[]>([]); // 제거 - 서버 페이지네이션 사용
  
  // 리뷰 모달 상태 관리
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState<any>(null);
  
  // 배송조회 모달 상태 관리
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [selectedCheckout, setSelectedCheckout] = useState<any>(null);
  
  // API 호출 중복 방지 ref
  const fetchCheckoutsRef = useRef(false);
  
  // 옵션 추출
  const {
    itemsPerPage = 10,
    // showPagination = true, // 현재 사용하지 않음
    // showSearch = true, // 현재 사용하지 않음
    // showFilter = true, // 현재 사용하지 않음
    title = t("주문 내역"),
    emptyText = t("주문 내역이 없습니다.")
  } = props.componentProps || {};
  
  // 관리자 모드 확인
  const isAdminMode = mode === 'editor' || props.isPreview;
  
  // 주문 내역 로드 함수
  const loadOrders = useCallback((page: number, take: number) => {
    // 이미 API 호출 중이면 중복 방지
    if (fetchCheckoutsRef.current) {
      return;
    }

    fetchCheckoutsRef.current = true;
    
    // @ts-ignore - Redux Toolkit 타입 오류 무시
    dispatch(fetchCheckouts({ page, take }))
      .finally(() => {
        fetchCheckoutsRef.current = false;
      });
  }, [dispatch]);
  
  // 컴포넌트 마운트 시 주문 내역 데이터 로드 - currentPage 변경 시에도 호출
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      loadOrders(currentPage, itemsPerPage);
    }
  }, [isUserLoggedIn, isAdminLoggedIn, currentPage, itemsPerPage, loadOrders]);
  
  // 날짜 범위 필터링 - 서버에서 필터링하므로 주석처리
  /*
  const getDateRangeFilter = useCallback((dateRange: string) => {
    const now = new Date();
    const startDate = new Date();
    
    switch (dateRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case '3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case '6months':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return null;
    }
    
    return startDate;
  }, []);
  */
  
  // checkouts 데이터를 필터링 및 정렬 - 클라이언트 사이드 필터링 제거
  // 서버에서 이미 페이지네이션된 데이터를 받으므로 추가 필터링 없이 사용
  const processedOrders = checkouts || [];
  
  // 주문 상태 텍스트
  const getStatusText = useCallback((status: string) => {
    const statusMap: Record<string, string> = {
      'REQUESTED': t('주문 요청됨'),
      'PENDING': t('결제 대기'),
      'PREPARING': t('상품 준비 중'),
      'PAID': t('결제 완료'),
      'SHIPPING': t('배송 중'),
      'ARRIVED': t('배송 도착'),
      'FINISHED': t('배송 완료'),
      'ISSUE': t('배송 문제'),
      'CANCELLED': t('주문 취소됨'),
      'CANCELED': t('주문 취소됨'),
      'REFUNDED': t('환불 완료')
    };
    return statusMap[status] || status;
  }, [t]);
  
  // 주문 상태에 따른 정보 반환
  const getStatusInfo = useCallback((status: string) => {
    const statusMap: Record<string, { text: string; class: string; icon: string }> = {
      'REQUESTED': { text: t('주문 요청됨'), class: 'status-requested', icon: '📋' },
      'PENDING': { text: t('결제 대기 중'), class: 'status-pending', icon: '⏳' },
      'PREPARING': { text: t('상품 준비 중'), class: 'status-preparing', icon: '📦' },
      'PAID': { text: t('결제 완료'), class: 'status-paid', icon: '💰' },
      'SHIPPING': { text: t('배송 중'), class: 'status-shipping', icon: '🚚' },
      'ARRIVED': { text: t('배송 도착'), class: 'status-arrived', icon: '🏠' },
      'FINISHED': { text: t('배송 완료'), class: 'status-completed', icon: '✅' },
      'ISSUE': { text: t('배송 문제 발생'), class: 'status-issue', icon: '⚠️' },
      'CANCELLED': { text: t('주문 취소됨'), class: 'status-cancelled', icon: '❌' },
      'CANCELED': { text: t('주문 취소됨'), class: 'status-cancelled', icon: '❌' },
      'REFUNDED': { text: t('환불 완료'), class: 'status-refunded', icon: '💸' }
    };

    return statusMap[status] || { text: status || t('알 수 없음'), class: 'status-unknown', icon: '❓' };
  }, [t]);
  
  // 날짜 포맷 함수
  const formatDate = useCallback((dateString: string): string => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const today = new Date();
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = weekdays[date.getDay()];

    const formattedDate = `${date.getMonth() + 1}/${date.getDate()}(${dayOfWeek})`;

    if (date.getFullYear() !== today.getFullYear()) {
      return `${date.getFullYear()}.${formattedDate}`;
    }

    return formattedDate;
  }, []);
  
  // 주문 상세 보기
  const handleViewDetail = useCallback((order: any) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  }, []);
  
  // 주문 취소
  const handleCancelOrder = useCallback(async (orderId: number) => {
    if (!window.confirm(t('정말로 이 주문을 취소하시겠습니까?'))) {
      return;
    }

    try {
      // TODO: 실제 주문 취소 API 호출
      // await dispatch(cancelOrder(orderId));
      
      // 임시로 알림만 표시
      alert(t('주문이 취소되었습니다.'));
      
      // 주문 목록 새로고침
      loadOrders(currentPage, itemsPerPage);
    } catch (error) {
      console.error('주문 취소 실패:', error);
      alert(t('주문 취소에 실패했습니다. 다시 시도해주세요.'));
    }
  }, [t, currentPage, itemsPerPage, loadOrders]);
  
  // 배송 추적
  const handleTrackShipment = useCallback((trackingNumber: string) => {
    if (trackingNumber) {
      window.open(`https://tracker.delivery/#/${trackingNumber}`, '_blank');
    }
  }, []);
  
  // 같은 상품들 장바구니 담기
  const handleAddToCartAgain = useCallback(async (order: any) => {
    if (!order.carts || order.carts.length === 0) {
      alert(t('장바구니에 담을 상품이 없습니다.'));
      return;
    }

    try {
      // 각 상품을 순차적으로 장바구니에 추가
      for (const cartItem of order.carts) {
        if (cartItem.product && cartItem.product.id) {
          const cartParams = {
            product_id: cartItem.product.id,
            count: cartItem.count || 1,
            options: cartItem.options || {}
          };

          // @ts-ignore - Redux Toolkit 타입 오류 무시
          await dispatch(addToCart(cartParams)).unwrap();
        }
      }

      alert(t('모든 상품이 장바구니에 담겼습니다.'));
    } catch (error) {
      console.error('장바구니 추가 실패:', error);
      alert(t('장바구니 추가에 실패했습니다. 다시 시도해주세요.'));
    }
  }, [dispatch, t]);
  
  // 페이지 변경
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // 리뷰 모달 열기
  const openReviewModal = useCallback((productId: number, orderId: number, product: any) => {
    setSelectedProduct(product);
    setSelectedOrderForReview(orderId);
    setReviewModalOpen(true);
  }, []);
  
  // 리뷰 제출 완료 후 호출될 함수
  const handleReviewSubmitted = useCallback(() => {
    // 필요한 경우 추가 로직 (예: 알림 표시)
  }, []);
  
  // 배송조회 모달 열기
  const openDeliveryModal = useCallback((checkout: any) => {
    setSelectedCheckout(checkout);
    setDeliveryModalOpen(true);
  }, []);

  return {
    data: {
      orders: processedOrders, // 서버에서 받은 데이터 그대로 사용
      loading,
      error,
      currentPage,
      totalPages: pagination?.totalPages || 1,
      totalOrders: pagination?.totalItems || processedOrders.length,
      searchQuery,
      selectedStatus,
      selectedDateRange,
      selectedOrder,
      showOrderDetail,
      isUserLoggedIn: !!user,
      isAdminMode,
      theme: props.theme || {},
      isMobile,
      deviceType,
      // 실제 데이터 구조에 맞는 추가 필드
      pagination: pagination || {
        currentPage,
        totalPages: 1,
        totalItems: processedOrders.length
      },
      // 원본 컴포넌트와 동일한 데이터
      checkouts: processedOrders, // 원본 호환성을 위해 추가
      itemsPerPage,
      title,
      emptyText,
      // 리뷰 모달 관련
      reviewModalOpen,
      selectedProduct,
      selectedOrderForReview,
      // 배송조회 모달 관련
      deliveryModalOpen,
      selectedCheckout
    },
    actions: {
      setSearchQuery,
      setSelectedStatus,
      setSelectedDateRange,
      handleViewDetail,
      handleCancelOrder,
      handleTrackShipment,
      handleAddToCartAgain, // 원본 이름으로 변경
      handlePageChange,
      setShowOrderDetail,
      getStatusText,
      getStatusInfo,
      formatDate,
      loadOrders,
      // 리뷰 모달 관련
      openReviewModal,
      handleReviewSubmitted,
      setReviewModalOpen,
      // 배송조회 모달 관련
      openDeliveryModal,
      setDeliveryModalOpen
    }
  };
};

export default useOrderHistoryLogic;